import { isEmpty, isNil } from 'lodash';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View as RNView, StyleSheet, TouchableOpacity } from 'react-native';
import { View } from '@shared/components/View';
import { MBCalendarDay } from '@shared/components/dateAndTime/calendar/day';
import { Range } from '@shared/components/dateAndTime/comboDatePicker/props';
import { MBIconV2 } from '@shared/components/image';
import { MBTextBody1Bold } from '@shared/components/typography';
import { useToday } from '@shared/scenes/cards/agenda/hooks';
import { ISOString } from '@shared/scenes/cards/agenda/utils';
import { Colors, grid } from '@shared/styles';

const WEEKDAYS = moment.weekdaysMin(true).map(s => s.slice(0, 1).toUpperCase());

function isWeekend(day: number) {
  return day === 0 || day === 6;
}

export enum RangeSelection {
  start = 'start',
  middle = 'middle',
  end = 'end',
  single = 'single',
}

function getRange(day: Moment, selectedRanges?: Range<ISOString>[]): RangeSelection | undefined {
  if (isNil(selectedRanges) || isEmpty(selectedRanges)) {
    return undefined;
  }
  for (const range of selectedRanges) {
    if (
      !isNil(range.start) &&
      day.isSame(range.start, 'day') &&
      !isNil(range.end) &&
      day.isSame(range.end, 'day')
    ) {
      return RangeSelection.single;
    }
    if (!isNil(range.start) && day.isSame(range.start, 'day')) {
      return RangeSelection.start;
    }
    if (!isNil(range.end) && day.isSame(range.end, 'day')) {
      return RangeSelection.end;
    }
    if (!isNil(range.end) && !isNil(range.start) && day.isBetween(range.start, range.end, 'day')) {
      return RangeSelection.middle;
    }
  }
  return undefined;
}

export interface CalendarDate {
  dateString: ISOString;
  label?: number;
  selected?: boolean;
  highlighted?: boolean;
  weekend?: boolean;
  today?: boolean;
  range?: RangeSelection;
  currentMonth?: boolean;
}

interface Props {
  maxDate?: ISOString;
  minDate?: ISOString;
  selectedDates?: ISOString[];
  selectedRanges?: Range<ISOString>[];
  showRangeMonth?: 'start' | 'end';
  onSelectDate?(date: ISOString): void;
  initialDate?: ISOString;
  highlightDates?: ISOString[];
  onMonthChange?: (monthStartISO: ISOString) => void;
}

export function MBCalendar({
  selectedDates,
  selectedRanges,
  showRangeMonth = 'start',
  maxDate,
  minDate,
  onSelectDate,
  initialDate,
  highlightDates,
  onMonthChange,
}: Props) {
  const today = useToday();
  const [currentMonth, setCurrentMonth] = useState<Moment>(
    moment(initialDate ? initialDate : today).startOf('month'),
  );

  useEffect(() => {
    if (!isEmpty(selectedDates)) {
      setCurrentMonth(moment(selectedDates?.[0]).startOf('month'));
    } else if (!isEmpty(selectedRanges)) {
      setCurrentMonth(
        moment(
          showRangeMonth === 'start' ? selectedRanges?.[0]?.start : selectedRanges?.[0]?.end,
        ).startOf('month'),
      );
    }
  }, [selectedDates, selectedRanges, showRangeMonth]);

  useEffect(() => {
    onMonthChange?.(moment(currentMonth).startOf('month').toISOString());
  }, [currentMonth, onMonthChange]);

  const nextMonthDisabled = useMemo(
    () => !isNil(maxDate) && moment(currentMonth).endOf('month').isAfter(maxDate),
    [maxDate, currentMonth],
  );
  const prevMonthDisabled = useMemo(
    () => !isNil(minDate) && moment(currentMonth).isBefore(minDate),
    [minDate, currentMonth],
  );
  const nextMonth = useCallback(() => {
    setCurrentMonth(moment(currentMonth).add(1, 'month'));
  }, [currentMonth]);
  const prevMonth = useCallback(() => {
    setCurrentMonth(moment(currentMonth).subtract(1, 'month'));
  }, [currentMonth]);

  const resetThisMonth = useCallback(
    () => setCurrentMonth(moment(today).startOf('month')),
    [today],
  );

  return (
    <View>
      <RNView style={styles.header}>
        <TouchableOpacity
          disabled={prevMonthDisabled}
          onPress={prevMonth}
          style={[styles.arrowButton, prevMonthDisabled && styles.hidden]}
        >
          <MBIconV2 name="arrow-point-left-heavy" color={Colors.v2.purple} size={grid(2)} />
        </TouchableOpacity>
        <TouchableOpacity onPress={resetThisMonth} style={styles.monthYear}>
          <MBTextBody1Bold>{currentMonth.format('MMMM YYYY')}</MBTextBody1Bold>
        </TouchableOpacity>
        <TouchableOpacity
          disabled={nextMonthDisabled}
          onPress={nextMonth}
          style={[styles.arrowButton, nextMonthDisabled && styles.hidden]}
        >
          <MBIconV2 name="arrow-point-right-heavy" color={Colors.v2.purple} size={grid(2)} />
        </TouchableOpacity>
      </RNView>
      <RNView style={styles.grid}>
        <RNView style={styles.row}>
          {WEEKDAYS.map((d, index) => (
            <RNView key={index} style={styles.cell}>
              <MBTextBody1Bold color={Colors.v2.gray30}>{d}</MBTextBody1Bold>
            </RNView>
          ))}
        </RNView>
        <Weeks
          currentMonth={currentMonth}
          minDate={minDate ?? ''}
          maxDate={maxDate ?? ''}
          onSelectDate={onSelectDate ?? (() => {})}
          selectedDates={selectedDates}
          highlightDates={highlightDates}
          selectedRanges={selectedRanges}
          today={today}
        />
      </RNView>
    </View>
  );
}

function Weeks({
  currentMonth,
  minDate,
  maxDate,
  onSelectDate,
  selectedDates,
  highlightDates,
  selectedRanges,
  today,
}: {
  currentMonth: Moment;
  minDate: ISOString;
  maxDate: ISOString;
  onSelectDate: (date: ISOString) => void;
  selectedDates?: ISOString[];
  highlightDates?: ISOString[];
  selectedRanges?: Range<ISOString>[];
  today?: ISOString;
}) {
  const weeks = generateSixWeekMonth(currentMonth.toISOString());
  return (
    <>
      {weeks.map((week, index) => (
        <Week
          key={index}
          week={week}
          minDate={minDate}
          maxDate={maxDate}
          onSelectDate={onSelectDate}
          selectedDates={selectedDates}
          highlightDates={highlightDates}
          selectedRanges={selectedRanges}
          today={today}
          currentMonth={currentMonth}
        />
      ))}
    </>
  );
}

function Week({
  week,
  minDate,
  maxDate,
  onSelectDate,
  selectedDates,
  highlightDates,
  selectedRanges,
  today,
  currentMonth,
}: {
  week: Moment[];
  minDate: ISOString;
  maxDate: ISOString;
  onSelectDate: (date: ISOString) => void;
  selectedDates?: ISOString[];
  highlightDates?: ISOString[];
  selectedRanges?: Range<ISOString>[];
  today?: ISOString;
  currentMonth: Moment;
}) {
  return (
    <RNView style={styles.row}>
      {week.map(day => {
        return (
          <Day
            key={day.toISOString()}
            date={day}
            minDate={minDate}
            maxDate={maxDate}
            onSelectDate={onSelectDate}
            selectedDates={selectedDates}
            highlightDates={highlightDates}
            selectedRanges={selectedRanges}
            today={today}
            currentMonth={currentMonth}
          />
        );
      })}
    </RNView>
  );
}

function Day({
  date,
  minDate,
  maxDate,
  onSelectDate,
  selectedDates,
  highlightDates,
  selectedRanges,
  today,
  currentMonth,
}: {
  date: Moment;
  minDate: ISOString;
  maxDate: ISOString;
  onSelectDate: (date: ISOString) => void;
  selectedDates?: ISOString[];
  highlightDates?: ISOString[];
  selectedRanges?: Range<ISOString>[];
  today?: ISOString;
  currentMonth: Moment;
}) {
  const day: CalendarDate = useMemo(() => {
    return {
      dateString: date.toISOString(),
      label: date.date(),
      selected: selectedDates?.some(selectedDate => date.isSame(selectedDate, 'day')),
      highlighted: highlightDates?.some(highlightDate => date.isSame(highlightDate, 'day')),
      range: getRange(date, selectedRanges),
      weekend: isWeekend(date.day()),
      currentMonth: date.isSame(currentMonth, 'month'),
      today: date.isSame(today, 'day'),
    };
  }, [date, selectedDates, highlightDates, selectedRanges, today, currentMonth]);
  const beforeMinDate = !!day && !!minDate && moment(day.dateString).isBefore(minDate);
  const afterMaxDate =
    !!day && !!maxDate && moment(day.dateString).endOf('day').isAfter(moment(maxDate).endOf('day'));
  return (
    <MBCalendarDay
      day={day}
      disabled={beforeMinDate || afterMaxDate}
      onPress={() => onSelectDate(day.dateString)}
    />
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    padding: grid(1),
  },
  arrowButton: {
    padding: grid(1),
  },
  hidden: {
    opacity: 0,
  },
  monthYear: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  grid: { flexDirection: 'column' },
  row: { flexDirection: 'row' },
  cell: {
    flex: 1,
    height: grid(5),
    justifyContent: 'center',
    alignItems: 'center',
  },
});

function generateSixWeekMonth(dateString: ISOString) {
  const startOfMonth = moment(dateString).startOf('month');
  const endOfMonth = moment(dateString).endOf('month');

  let startOfCalendar = startOfMonth.clone().startOf('week');

  // when feb starts on first, don't put 2 full weeks at end
  if (startOfCalendar.date() === 1 && startOfMonth.month() === 1) {
    startOfCalendar = startOfCalendar.subtract(1, 'week');
  }

  let endOfCalendar = endOfMonth.clone().endOf('week');

  while (endOfCalendar.diff(startOfCalendar, 'days') < 41) {
    endOfCalendar.add(1, 'week');
  }

  const weeks: Moment[][] = [];
  let currentDay = startOfCalendar.clone();

  for (let i = 0; i < 6; i++) {
    const week: Moment[] = [];
    for (let j = 0; j < 7; j++) {
      week.push(currentDay.clone());
      currentDay.add(1, 'day');
    }
    weeks.push(week);
  }

  return weeks;
}
